<script setup lang="ts">
// NPM
import { computed, onBeforeMount, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Modal } from '@magma-app/magma-lapilli';

// Components
import {
  BaseBadge,
  BaseEmptyState,
  FieldTabs,
  BaseCard,
  BaseButton,
  BaseLoader
} from '@magma-app/magma-lapilli';
import BaseRelationForList from '@/components/bases/BaseRelationForList.vue';
import TheSidebar from '@/components/shell/TheSidebar.vue';
import RelationsZeroState from '@/components/relations/RelationsZeroState.vue';

// Types

// Stores
import { useRelationsStore } from '@/stores/relations';
import { useUserStore } from '@/stores/user';
import { useChallengesStore } from '@/stores/challenges';
import { useMq } from 'vue3-mq';
import type { IChallenge } from '@/types/IChallenge';
import { useCommonStore } from '@/stores/common';
import { useChallenges } from '@/api/challenges/use-challenges';
import { useStartChallenge } from '@/api/challenges/use-start-challenge';
import type { UserDashboardRelationsGetHelperRelationsResponseType } from '@magma-app/magma-types/all';
const relationsStore = useRelationsStore();
const userStore = useUserStore();
const challengesStore = useChallengesStore();
const commonStore = useCommonStore();

// Plugins
const route = useRoute();
const router = useRouter();
const { t } = useI18n({ useScope: 'global' });
const mq = useMq();

// Data
/**
 * Cut on who the ball is with, not on an opaque status:
 *   toProcess -> you have something to do
 *   ongoing   -> no ambassador action; awaiting confirmation/review or exchange closed without approval
 *   finished  -> exchange approved or refused, regardless of the final application decision
 */
const tabs = computed(() => [
  {
    label: `${t('relationsLayout.tabs.todo')} ${
      relationsStore.relations.toProcess?.length || 0
    }`,
    value: 'toProcess'
  },
  {
    label: `${t('relationsLayout.tabs.waiting')} ${
      relationsStore.relations.ongoing?.length || 0
    }`,
    value: 'ongoing'
  },
  {
    label: `${t('relationsLayout.tabs.done')} ${
      relationsStore.relations.finished?.length || 0
    }`,
    value: 'finished'
  }
]);

// Computed
const hasRelations = computed(() => {
  const relations = relationsStore.relations;

  return Boolean(
    relations.finished?.length ||
      relations.ongoing?.length ||
      relations.toProcess?.length
  );
});

/**
 * Urgency, not recency. Sorting "A faire" by `updatedAt` desc put the freshest match on top and
 * buried the one relation that is blocking someone else.
 */
const URGENCY: Record<string, number> = {
  toConfirm: 0, // the prospect confirmed and is waiting on the ambassador
  toContact: 1,
  contacted: 2
};

const byUpdatedAtDesc = (a: { updatedAt: Date }, b: { updatedAt: Date }) =>
  new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();

const relations = computed(() => {
  if (currentTab.value === 'toProcess') {
    return relationsStore.relations.toProcess.slice().sort((a, b) => {
      const rank = (URGENCY[a.state] ?? 9) - (URGENCY[b.state] ?? 9);
      if (rank !== 0) return rank;

      // then whatever has been quiet the longest
      if (a.isInactive !== b.isInactive) return a.isInactive ? -1 : 1;

      return byUpdatedAtDesc(a, b);
    });
  } else if (currentTab.value === 'ongoing') {
    return relationsStore.relations.ongoing.slice().sort(byUpdatedAtDesc);
  } else if (currentTab.value === 'finished') {
    return relationsStore.relations.finished.slice().sort(byUpdatedAtDesc);
  } else {
    return [];
  }
});

const statuses: ('finished' | 'ongoing' | 'toProcess')[] = [
  'finished',
  'ongoing',
  'toProcess'
];

// Most ambassadors are on a phone, where the two panes are a master/detail pair rather than
// columns. Rendering conditionally (instead of `hidden lg:flex`) keeps one tree mounted.
const hasSelection = computed(() => !!route?.params.relationId);
const showList = computed(() => mq.lgPlus || !hasSelection.value);
const showDetail = computed(
  () => hasRelations.value && (mq.lgPlus || hasSelection.value)
);
const hasAvailableChallenges = computed(() =>
  Boolean(challenges.value?.available?.length)
);

// Reactive variables
const challengeId = ref<number | null>(null);
const challengeOpen = ref(false);
const { data: challenges, isLoading } = useChallenges();
const { mutateAsync: startChallenge, isPending: isStartingChallenge } =
  useStartChallenge();

// Reactive variables
const modal = ref<number | null>(null);
const currentModalStep = ref<'start' | 'confirm' | 'end'>('start');

// Reactive variables
const initialized = ref(false);
const relationsLoadFailed = ref(false);
const currentTab = ref<'finished' | 'ongoing' | 'toProcess'>('toProcess');

// Lifecycle hooks
const loadRelations = async () => {
  initialized.value = false;
  relationsLoadFailed.value = false;

  try {
    await relationsStore.getRelations();
  } catch {
    relationsLoadFailed.value = true;
    initialized.value = true;
    return;
  }

  if (route?.params.relationId) {
    let found = false;
    for (const status of statuses) {
      if (findRelationStatus(status)) {
        currentTab.value = status;
        found = true;
        break;
      }
    }

    if (!found) {
      commonStore.setAlert({
        open: true,
        type: 'error',
        title: t('actionDeepLink.notFound.title'),
        description: t('actionDeepLink.notFound.relation'),
        duration: 5000
      });
      await router.replace({ name: 'relations-empty-state' });
    }
  }

  initialized.value = true;
};

onBeforeMount(loadRelations);

// Watchers
watch(route, async (newRoute) => {
  if (newRoute?.params.relationId) {
    for (const status of statuses) {
      if (findRelationStatus(status)) {
        currentTab.value = status;
        break;
      }
    }
  }
});

// Event Listeners
const onRelationClick = async (
  relation: UserDashboardRelationsGetHelperRelationsResponseType['toProcess'][number]
) => {
  // Mark relation as read
  if (!relation.isRead) {
    await relationsStore.markRelationMomentAs({
      relationMomentId: relation.relationMomentId,
      role: 'read'
    });
    relation.isRead = true;

    await relationsStore.getRelations();
  }

  // Go to relation
  router.push({
    name: 'helper-relation-overview',
    params: {
      relationId: relation.relationId
    }
  });
};

// Methods
const findRelationStatus = (status: 'finished' | 'ongoing' | 'toProcess') => {
  return relationsStore.relations[status].some(
    (relation) => relation.relationId === Number(route?.params.relationId)
  );
};
</script>

<template>
  <div class="flex bg-background-subtle" data-cy="relations-layout">
    <TheSidebar />

    <div
      v-if="showList"
      class="flex min-w-0 w-full flex-col bg-background-default pt-2 lg:ml-20 lg:mt-3 lg:rounded-tl-[32px] lg:border lg:border-border-subtle lg:border-b-transparent lg:pt-8 2xl:ml-64"
      :class="{ 'lg:w-[340px] lg:shrink-0 2xl:w-[400px]': hasRelations }"
    >
      <div v-if="!challengeOpen" class="mb-4 px-4 md:px-8 lg:mb-8">
        <h2 class="text-2xl font-bold leading-7 text-foreground-emphasis">
          Relations
        </h2>
      </div>

      <div
        v-if="!initialized"
        class="flex min-h-[420px] flex-1 items-center justify-center"
        role="status"
        :aria-label="$t('relationsLayout.loading')"
      >
        <BaseLoader />
      </div>

      <div
        v-else-if="relationsLoadFailed"
        class="mx-auto flex min-h-[420px] max-w-md flex-col items-center justify-center px-6 text-center"
      >
        <h3 class="text-mgm-txt-lg font-semibold text-foreground-emphasis">
          {{ $t('relationsLayout.loadError.title') }}
        </h3>
        <p class="mt-2 text-mgm-txt-sm text-foreground-default">
          {{ $t('relationsLayout.loadError.description') }}
        </p>
        <BaseButton class="mt-5" @click="loadRelations">
          {{ $t('relationsLayout.loadError.retry') }}
        </BaseButton>
      </div>

      <template v-else-if="hasRelations && !challengeOpen">
        <FieldTabs
          v-model="currentTab"
          class="mx-4 mb-2 flex justify-between overflow-x-auto md:mx-8 md:justify-start lg:mb-4"
          :tabs="tabs"
          data-cy="relations-tab"
          @update:model-value="router.push({ name: 'relations-empty-state' })"
        />
        <div class="flex-1">
          <template v-if="relations?.length">
            <BaseRelationForList
              v-for="(relation, relationIndex) in relations"
              :key="relationIndex"
              data-cy="relation"
              :relation="relation"
              :active="
                relation.relationId ===
                parseInt($route?.params.relationId as string)
              "
              @click="onRelationClick(relation)"
            />
          </template>

          <BaseEmptyState
            v-else
            class="flex h-full flex-col items-center justify-center"
            :text="$t(`relationsLayout.empty.${currentTab}`)"
            type="empty-list"
          />
        </div>
      </template>

      <div v-else>
        <RelationsZeroState v-if="!challengeOpen" />

        <div
          class="mx-auto w-full max-w-[1200px] px-4 md:px-8"
          :class="challengeOpen ? '' : hasAvailableChallenges ? 'pb-10' : ''"
        >
          <h4
            v-if="!challengeOpen && hasAvailableChallenges"
            class="text-mgm-txt-md font-semibold text-foreground-emphasis"
          >
            {{ $t(`relationsLayout.discoverChallenge.title`) }}
          </h4>
          <p
            v-if="!challengeOpen && hasAvailableChallenges"
            class="mb-6 mt-1 text-mgm-txt-sm text-foreground-default"
          >
            {{ $t(`relationsLayout.discoverChallenge.subtitle`) }}
          </p>
          <div
            class="block h-full flex-1"
            :class="{
              'content-start lg:flex lg:flex-wrap': challenges?.available.length
            }"
          >
            <template v-if="challenges?.available.length">
              <Modal
                v-for="(challenge, challengeIndex) in challenges?.available"
                :key="challengeIndex"
                :open="modal === challenge.challengeId"
                content-class="max-w-[90dvw] sm:max-w-[480px]"
                @open-focus="(e) => e.preventDefault()"
                @open-change="
                  (open) => {
                    modal = challenge.challengeId;

                    if (!open) {
                      modal = null;
                    }
                  }
                "
              >
                <template #trigger>
                  <BaseCard
                    class="mb-2 lg:mb-6 lg:mr-6"
                    :title="challenge.name"
                    :description="challenge.description"
                    :img-url="
                      challenge.pictureUrl
                        ? challenge.pictureUrl
                        : mq.lgPlus
                          ? '/img/challenge_lg.png'
                          : '/img/challenge.png'
                    "
                    :points="challenge.points"
                    :badge-status="{
                      points: challenge.points,
                      type: 'challenge'
                    }"
                    :display-points="!challenge.pictureUrl"
                  />
                </template>
                <template #content>
                  <div v-if="currentModalStep === 'start'">
                    <div
                      class="w-12 h-12 bg-[#F1F5F9] rounded-full border-8 border-[#F8FAFC] flex items-center justify-center mb-5"
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 16V12M12 8H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                          stroke="#5F6C85"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </div>
                    <h3
                      class="text-mgm-txt-lg font-semibold text-foreground-emphasis"
                    >
                      {{ challenge.name }}
                    </h3>
                    <BaseBadge
                      class="mt-2"
                      size="md"
                      :text="challenge.points"
                      icon-type="icon"
                      icon-name="star"
                    />
                    <div
                      class="rich-text-content text-mgm-txt-sm text-foreground-default my-5 overflow-hidden max-h-[40dvh] lg:max-h-auto overflow-y-auto"
                      v-html="challenge.description"
                    ></div>
                    <BaseButton
                      size="lg"
                      class="w-full"
                      @click="currentModalStep = 'confirm'"
                      >{{ $t('challenges.available.start.cta') }}</BaseButton
                    >
                  </div>
                  <div v-if="currentModalStep === 'confirm'">
                    <div
                      class="w-12 h-12 bg-[#F1F5F9] rounded-full border-8 border-[#F8FAFC] flex items-center justify-center mb-5"
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 16V12M12 8H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                          stroke="#5F6C85"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </div>
                    <h3
                      class="text-mgm-txt-lg font-semibold text-foreground-emphasis"
                    >
                      {{ $t('challenges.available.confirm.title') }}
                    </h3>
                    <p
                      class="text-mgm-txt-sm text-foreground-default my-5 overflow-hidden"
                    >
                      {{
                        $t('challenges.available.confirm.content', {
                          email: challenge.contactAdminEmail
                        })
                      }}
                    </p>
                    <div class="flex gap-2">
                      <BaseButton
                        size="lg"
                        class="flex-1"
                        state="scdr"
                        :disabled="isStartingChallenge"
                        @click="
                          () => {
                            currentModalStep = 'start';
                            modal = null;
                          }
                        "
                        >{{ $t('generic.cancel') }}</BaseButton
                      >
                      <BaseButton
                        size="lg"
                        class="flex-1"
                        :disabled="isStartingChallenge"
                        @click="
                          async () => {
                            await startChallenge(challenge.challengeId);
                            currentModalStep = 'end';
                          }
                        "
                        >{{
                          $t('challenges.available.confirm.cta')
                        }}</BaseButton
                      >
                    </div>
                  </div>
                  <div v-if="currentModalStep === 'end'">
                    <div
                      class="w-12 h-12 bg-[#D1FADF] rounded-full border-8 border-[#ECFDF3] flex items-center justify-center mb-5"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999M22 3.99999L12 14.01L9.00001 11.01"
                          stroke="#03874C"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </div>
                    <h3
                      class="text-mgm-txt-lg font-semibold text-foreground-emphasis"
                    >
                      {{ $t('challenges.available.end.title') }}
                    </h3>
                    <ul
                      class="list-disc pl-0 text-mgm-txt-sm text-foreground-default my-4"
                    >
                      <li>
                        {{
                          $t('challenges.available.end.content.first', {
                            email: challenge.contactAdminEmail
                          })
                        }}
                      </li>
                      <li>
                        {{ $t('challenges.available.end.content.second') }}
                      </li>
                    </ul>
                    <BaseButton
                      size="lg"
                      class="w-full"
                      @click="
                        () => {
                          currentModalStep = 'start';
                          modal = null;
                        }
                      "
                      >{{ $t('generic.close') }}</BaseButton
                    >
                  </div>
                </template>
              </Modal>
            </template>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="showDetail"
      class="min-w-0 w-full flex-1 border border-border-subtle border-b-transparent border-l-transparent bg-background-default lg:mt-3"
      :class="{ 'items-start': hasSelection }"
    >
      <routerView v-if="initialized" />
    </div>
  </div>
</template>
